import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logoTransparent from "../../assets/images/logo/logo_transparent.png";
import theme from "../../theme/theme";
import {
  UserIcon,
  FlaggedIcon,
  FileIcon,
  AnswerModalIcon,
  PastAnswerModalIcon,
  StaticsIcon,
  ProfileIcon,
  SettingIcon,
  PaymentIcon,
  SubscriptionIcon,
  PlansManagementIcon,
  VerticallyThreeDots,
} from "../../assets/icons";

const Sidebar = ({
  isOpen,
  onClose,
  currentPath,
  onLogout,
  isMobile = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showMenu, setShowMenu] = useState(false);

  const menuItems = useMemo(
    () => [
      {
        id: "dashboard",
        label: "Dashboard",
        path: "/dashboard",
        icon: <StaticsIcon />,
        isDefault: true,
      },
      {
        id: "users",
        label: "Users Management",
        path: "/users",
        icon: <UserIcon />,
        isDefault: true,
      },
      {
        id: "upload",
        label: "Model Answer",
        path: "/modal-answer",
        icon: <AnswerModalIcon />,
      },
      {
        id: "pastPaper",
        label: "Past Model Answer",
        path: "/past-modal-answer",
        icon: <PastAnswerModalIcon />,
      },
      {
        id: "submissions",
        label: "Submissions",
        path: "/submissions",
        icon: <FileIcon />,
      },
      {
        id: "flagged",
        label: "Flagged Essays",
        path: "/flagged",
        icon: <FlaggedIcon />,
      },
      {
        id: "subscription",
        label: "Subscription",
        path: "/payment-history",
        icon: <SubscriptionIcon />,
      },
      {
        id: "plans",
        label: "Plans Management",
        path: "/plans",
        icon: <PlansManagementIcon />,
      },
    ],
    []
  );

  useEffect(() => {
    const currentPath = location.pathname;
    const currentMenuItem = menuItems.find((item) => item.path === currentPath);
    if (currentMenuItem) {
      setActiveTab(currentMenuItem.id);
    } else if (currentPath === "/plans") {
      setActiveTab("plans");
    } else if (currentPath === "/payment-history" || currentPath === "/payments") {
      setActiveTab("payments");
    } else if (currentPath === "/" || currentPath === "/dashboard") {
      setActiveTab("dashboard");
    }
  }, [location.pathname, menuItems]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest(".menu-container")) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);
  const handleNavigation = (path, itemId) => {
    setActiveTab(itemId);
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const handleMenuOption = (option) => {
    setShowMenu(false);
    switch (option) {
      case "profile":
        navigate("/profile");
        break;
      case "settings":
        navigate("/settings");
        break;
      case "payments":
        navigate("/payments");
        break;
      case "plans":
        navigate("/plans");
        break;
      default:
        break;
    }
    if (isMobile) {
      onClose();
    }
  };

  if (isMobile) {
    return (
      <>
        {isOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
            onClick={onClose}
          />
        )}

        <div
          className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${
            isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center hover:opacity-80 transition-opacity duration-200"
              >
                <img
                  src={logoTransparent}
                  alt="Calbar Logo"
                  className="h-12 w-auto"
                />
              </button>
              <button
                onClick={onClose}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              ></button>
            </div>
            <nav className="flex-1 px-4 py-6 space-y-2">
              {menuItems.map((item) => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.path, item.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors duration-200 ${
                      isActive
                        ? "text-primary-700"
                        : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                    style={{
                      borderLeft: isActive
                        ? `4px solid ${theme.Colors.primary}`
                        : "none",
                      backgroundColor: isActive ? "#ededed" : "transparent",
                    }}
                  >
                    <span
                      className={`${
                        isActive ? "text-primary-600" : "text-gray-400"
                      }`}
                    >
                      {item.icon}
                    </span>
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="p-4 border-t border-gray-200">
        <div className="flex items-center justify-between space-x-2">
          <button
            onClick={() => {
              onLogout();
            }}
            className="flex-1 flex items-center justify-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-200"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span className="font-medium">Logout</span>
          </button>

          <div className="relative menu-container">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="flex items-center justify-center px-3 py-3 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
            >
              <VerticallyThreeDots />
            </button>

            {showMenu && (
              <div className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[150px]">
                <button
                  onClick={() => handleMenuOption("profile")}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  <ProfileIcon width={14} height={14} />
                  <span>Profile</span>
                </button>
                <button
                  onClick={() => handleMenuOption("settings")}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  <SettingIcon />
                  <span>Settings</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
          </div>
        </div>
      </>
    );
  }
  return (
    <div className="w-64 bg-white shadow-lg border-r border-gray-200 flex flex-col">
      <div className="flex items-center p-4 border-b border-gray-200">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center justify-center w-full hover:opacity-80 transition-opacity duration-200"
        >
          <img
            src={logoTransparent}
            alt="Calbar Logo"
            className="h-12 w-auto"
          />
        </button>
      </div>
      <nav className="flex-1 px-4 py-6 space-y-2">
        {menuItems.map((item) => {
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path, item.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors duration-200 ${
                isActive
                  ? "text-primary-700"
                  : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
              }`}
              style={{
                borderLeft: isActive
                  ? `4px solid ${theme.Colors.primary}`
                  : "none",
                backgroundColor: isActive ? "#ededed" : "transparent",
              }}
            >
              <span
                className={`${isActive ? "text-primary-600" : "text-gray-400"}`}
              >
                {item.icon}
              </span>
              <span className="font-medium">{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center justify-between space-x-2">
          {/* Logout Button */}
          <button
            onClick={() => {
              onLogout();
            }}
            className="flex-1 flex items-center justify-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-200"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span className="font-medium">Logout</span>
          </button>

          {/* Three-dot Menu */}
          <div className="relative menu-container">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="flex items-center justify-center px-3 py-3 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
            >
              <VerticallyThreeDots />
            </button>

            {showMenu && (
              <div className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[150px]">
                <button
                  onClick={() => handleMenuOption("profile")}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  <ProfileIcon width={14} height={14} />
                  <span>Profile</span>
                </button>
                <button
                  onClick={() => handleMenuOption("settings")}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  <SettingIcon />
                  <span>Settings</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
